import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { errorMessage, isApiError } from '@/api/client';
import { useDashboard, useReports, useSaveReport } from '@/api/hooks';
import { useAuth } from '@/auth/context';
import { Combobox } from '@/components/Combobox';
import { useToast } from '@/components/toast-context';
import { Button, Card, Checkbox, ErrorNote, PageHeader, SelectField, Skeleton, Switch, TextField } from '@/components/ui';
import { readRange } from '@/features/dashboard/range';
import { DestinationPicker } from '@/features/destinations/DestinationPicker';
import { NotFound } from '@/features/NotFound';
import { cx } from '@/lib/cx';
import { ordinal, WEEKDAYS } from '@/lib/format';
import { browserTimezone, timezoneLabel, timezones } from '@/lib/timezones';
import { COVERS, emptyDraft, fromReport, STEPS, toInput, validateAll, validateStep, type ReportDraft, type Step } from './draft';

export function ReportWizard() {
  const { reportId } = useParams();
  const editing = !!reportId;
  const { user } = useAuth();
  const reports = useReports();
  const existing = reports.data?.find((r) => r.id === reportId);
  if (editing && reports.isPending) return <Skeleton className="h-96" />;
  if (editing && reports.data && !existing) return <NotFound />;
  return <WizardForm key={existing?.id ?? 'new'} initial={existing ? fromReport(existing) : emptyDraft(user?.timezone ?? browserTimezone())} reportId={reportId} />;
}

function WizardForm({ initial, reportId }: { initial: ReportDraft; reportId?: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const save = useSaveReport();
  const dash = useDashboard(readRange());
  const [draft, setDraft] = useState<ReportDraft>(initial);
  const [step, setStep] = useState<Step>(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const set = <K extends keyof ReportDraft>(k: K, v: ReportDraft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const mounted = useRef(false);
  useEffect(() => {
    // Move focus to the new step's heading (not on first render, where the name field has focus).
    if (mounted.current) headingRef.current?.focus();
    mounted.current = true;
  }, [step]);

  function next() {
    const e = validateStep(step, draft);
    setErrors(e);
    if (Object.keys(e).length) return;
    setStep((s) => Math.min(2, s + 1) as Step);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (step < 2) {
      next();
      return;
    }
    const invalid = validateAll(draft);
    if (invalid) {
      setStep(invalid.step);
      setErrors(invalid.errors);
      return;
    }
    setServerError(null);
    try {
      const r = await save.mutateAsync({ id: reportId, input: toInput(draft) });
      toast({ title: reportId ? 'Update saved' : 'Update scheduled', description: `“${r.name}” · ${COVERS[r.frequency]}` });
      navigate('/updates');
    } catch (err) {
      if (isApiError(err) && Object.keys(err.fields).length) {
        setErrors(err.fields);
        const s = (['name', 'widget_ids'].some((k) => k in err.fields) ? 0 : ['destination_ids'].some((k) => k in err.fields) ? 2 : 1) as Step;
        setStep(s);
      }
      setServerError(errorMessage(err));
    }
  }

  const widgets = dash.data?.widgets ?? [];

  return (
    <div className="mx-auto max-w-2xl">
      <Link to="/updates" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-ink hover:text-ink">
        <ArrowLeft className="size-4" aria-hidden /> Updates
      </Link>
      <PageHeader title={reportId ? 'Edit update' : 'New update'} description="What → How often → Where" />

      <ol className="mb-6 flex items-center gap-2 text-sm" aria-label="Steps">
        {STEPS.map((label, i) => (
          <li key={label} className="flex items-center gap-2" aria-current={i === step ? 'step' : undefined}>
            <span
              className={cx(
                'flex size-6 items-center justify-center rounded-full text-xs font-semibold',
                i < step ? 'bg-ink text-paper' : i === step ? 'bg-yellow text-yellow-ink' : 'bg-muted text-muted-ink',
              )}
            >
              {i < step ? <Check className="size-3.5" aria-hidden /> : i + 1}
            </span>
            <span className={i === step ? 'font-medium text-ink' : 'text-muted-ink'}>{label}</span>
            {i < STEPS.length - 1 && <span aria-hidden className="mx-1 h-px w-6 bg-border sm:w-10" />}
          </li>
        ))}
      </ol>

      <form onSubmit={submit} noValidate>
        <Card className="p-5">
          <h2 ref={headingRef} tabIndex={-1} className="mb-4 text-lg font-semibold outline-none focus-visible:shadow-none">
            {step === 0 ? 'What should it include?' : step === 1 ? 'How often?' : 'Where should it go?'}
          </h2>
          {serverError && (
            <div className="mb-4">
              <ErrorNote message={serverError} />
            </div>
          )}

          {step === 0 && (
            <div className="flex flex-col gap-5">
              <TextField label="Name" value={draft.name} onChange={(e) => set('name', e.target.value)} error={errors.name} placeholder="Morning numbers" autoFocus />
              <fieldset aria-describedby={errors.widget_ids ? 'widgets-error' : undefined}>
                <legend className="text-sm font-medium">KPIs</legend>
                <div className="mt-3 flex flex-col gap-3">
                  <label className="flex items-center gap-3 text-sm">
                    <input type="radio" name="scope" className="size-4 accent-[var(--md-ink)]" checked={draft.allWidgets} onChange={() => set('allWidgets', true)} />
                    Every KPI on my dashboard <span className="text-muted-ink">(stays in sync as you add more)</span>
                  </label>
                  <label className="flex items-center gap-3 text-sm">
                    <input type="radio" name="scope" className="size-4 accent-[var(--md-ink)]" checked={!draft.allWidgets} onChange={() => set('allWidgets', false)} />
                    Only the ones I pick
                  </label>
                </div>
                {!draft.allWidgets && (
                  <div className="mt-4 grid gap-3 rounded-control border border-border p-4 sm:grid-cols-2">
                    {dash.isPending && <Skeleton className="h-20 sm:col-span-2" />}
                    {widgets.map((w) => (
                      <Checkbox
                        key={w.id}
                        label={w.label}
                        description={`${w.integration_name} · ${w.connection_name}`}
                        checked={draft.widget_ids.includes(w.id)}
                        onChange={(e) =>
                          set('widget_ids', e.target.checked ? [...draft.widget_ids, w.id] : draft.widget_ids.filter((x) => x !== w.id))
                        }
                      />
                    ))}
                    {dash.data && widgets.length === 0 && <p className="text-sm text-muted-ink">Your dashboard has no KPIs yet.</p>}
                  </div>
                )}
                {errors.widget_ids && (
                  <p id="widgets-error" className="mt-2 text-xs font-medium text-down">
                    {errors.widget_ids}
                  </p>
                )}
              </fieldset>
            </div>
          )}

          {step === 1 && (
            <div className="flex flex-col gap-5">
              <fieldset>
                <legend className="text-sm font-medium">Frequency</legend>
                <div className="mt-3 grid gap-2 sm:grid-cols-3">
                  {(['daily', 'weekly', 'monthly'] as const).map((f) => (
                    <label
                      key={f}
                      className={cx(
                        'flex cursor-pointer flex-col rounded-control border p-3 text-sm has-[:focus-visible]:ring-3 has-[:focus-visible]:ring-yellow',
                        draft.frequency === f ? 'border-ink bg-card' : 'border-border hover:bg-muted',
                      )}
                    >
                      <input type="radio" name="frequency" value={f} checked={draft.frequency === f} onChange={() => set('frequency', f)} className="sr-only" />
                      <span className="font-medium">{f === 'daily' ? 'Daily' : f === 'weekly' ? 'Weekly' : 'Monthly'}</span>
                      <span className="text-xs text-muted-ink">{COVERS[f]}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
              <div className="grid gap-4 sm:grid-cols-2">
                {draft.frequency === 'weekly' && (
                  <SelectField label="Day" value={draft.weekday ?? 1} onChange={(e) => set('weekday', Number(e.target.value))} error={errors.weekday}>
                    {WEEKDAYS.map((d, i) => (
                      <option key={d} value={i + 1}>
                        {d}
                      </option>
                    ))}
                  </SelectField>
                )}
                {draft.frequency === 'monthly' && (
                  <SelectField
                    label="Day of the month"
                    value={draft.day_of_month ?? 1}
                    onChange={(e) => set('day_of_month', Number(e.target.value))}
                    error={errors.day_of_month}
                    hint="Up to the 28th, so it runs every month."
                  >
                    {Array.from({ length: 28 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {ordinal(i + 1)}
                      </option>
                    ))}
                  </SelectField>
                )}
                <TextField label="Time" type="time" value={draft.time_of_day} onChange={(e) => set('time_of_day', e.target.value.slice(0, 5))} error={errors.time_of_day} step={300} />
              </div>
              <Combobox label="Time zone" value={draft.timezone} options={timezones()} onChange={(v) => set('timezone', v)} format={timezoneLabel} />
              {errors.timezone && <p className="text-xs font-medium text-down">{errors.timezone}</p>}
            </div>
          )}

          {step === 2 && (
            <div className="flex flex-col gap-5">
              <DestinationPicker value={draft.destination_ids} onChange={(ids) => set('destination_ids', ids)} error={errors.destination_ids} />
              <Switch label="Turn it on now" checked={draft.enabled} onChange={(v) => set('enabled', v)} />
            </div>
          )}
        </Card>

        <div className="mt-4 flex flex-wrap justify-between gap-2">
          <Button onClick={() => (step === 0 ? navigate('/updates') : setStep((s) => (s - 1) as Step))} icon={<ArrowLeft className="size-4" aria-hidden />}>
            {step === 0 ? 'Cancel' : 'Back'}
          </Button>
          {step < 2 ? (
            <Button type="submit" variant="primary">
              Next <ArrowRight className="size-4" aria-hidden />
            </Button>
          ) : (
            <Button type="submit" variant="primary" loading={save.isPending}>
              {reportId ? 'Save changes' : 'Schedule update'}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
